"use client";

import { useState, useId } from "react";
import Image from "@/components/ui/image";
import { 
  LayoutDashboard, MessageSquare, Bot, 
  Workflow, Plug, Settings, HelpCircle, 
  Search, Bell, Sun, Moon, Plus, Circle, Calendar, ChevronRight, 
  TrendingUp, Clock, Home, Phone, Inbox, BarChart3, AlertTriangle,
  Filter, RefreshCw, Download, Eye, ChevronDown, Target, Smile, Star
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ayraLogoDark from "@/assets/ayra-logo-dark.png";
import ayraLogoLight from "@/assets/ayra-logo-light.png";

type ViewType = 'dashboard' | 'calls' | 'analytics';

// Sample call data
const callData = [
  { id: 1, phone: "+1 (647) 830-8026", dateTime: "Today 4:57 PM", duration: "6m 14s", status: "Completed" },
  { id: 2, phone: "+1 (437) 781-8491", dateTime: "Today 4:08 PM", duration: "3m 44s", status: "Completed" },
  { id: 3, phone: "+1 (416) 389-5385", dateTime: "Today 8:21 AM", duration: "2m 22s", status: "Completed" },
  { id: 4, phone: "+1 (416) 697-9392", dateTime: "Today 7:30 AM", duration: "0m 3s", status: "Completed" },
  { id: 5, phone: "+1 (416) 803-5767", dateTime: "Today 6:44 AM", duration: "1m 47s", status: "Completed" },
];

const BusinessDashboardMockup = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeView, setActiveView] = useState<ViewType>('dashboard');
  const glowId = useId().replace(/:/g, '');
  const filters = {
    unopaq: `unopaq-${glowId}`,
    unopaq2: `unopaq2-${glowId}`,
    unopaq3: `unopaq3-${glowId}`,
  };

  // Theme colors
  const theme = {
    // Backgrounds
    bg: isDarkMode ? 'bg-[#0d0d0d]' : 'bg-[#fafafa]',
    bgPanel: isDarkMode ? 'bg-[#141414]' : 'bg-white',
    bgCard: isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white',
    bgSearch: isDarkMode ? 'bg-[#1f1f1f]' : 'bg-[#f5f5f5]',
    bgWelcome: isDarkMode ? 'bg-gradient-to-r from-[#1a1a1a] to-[#0d0d0d]' : 'bg-gradient-to-r from-[#f0fdf4] to-[#fafafa]',
    bgHover: isDarkMode ? 'hover:bg-[#2a2a2a]' : 'hover:bg-muted/50',
    bgActive: isDarkMode ? 'bg-primary/20' : 'bg-primary/10',
    // Text
    text: isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground',
    textMuted: isDarkMode ? 'text-[#888888]' : 'text-muted-foreground',
    textMutedSoft: isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60',
    // Borders
    border: isDarkMode ? 'border-[#2a2a2a]' : 'border-border/30',
    borderSoft: isDarkMode ? 'border-[#222222]' : 'border-border/20',
    borderCard: isDarkMode ? 'border-[#2a2a2a]' : 'border-border/50',
    // Shadows
    shadow: isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.5)]' : 'shadow-2xl',
    // Charts/bars
    barBg: isDarkMode ? 'bg-primary/30' : 'bg-primary/20',
    barActive: isDarkMode ? 'bg-primary' : 'bg-primary',
  };

  return (
    <motion.div 
      className="w-full max-w-7xl mx-auto group"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* SVG Filters for Glow Effect */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq}>
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 9 0" />
        </filter>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq2}>
          <feColorMatrix values="1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 3 0" />
        </filter>
        <filter width="300%" x="-100%" height="300%" y="-100%" id={filters.unopaq3}>
          <feColorMatrix values="1 0 0 0.2 0 0 1 0 0.2 0 0 0 1 0.2 0 0 0 0 2 0" />
        </filter>
      </svg>

      {/* Dashboard Container */}
      <motion.div 
        className={`relative rounded-xl md:rounded-2xl overflow-hidden transition-all duration-300 ease-in-out bg-[#111215]`}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        {/* Browser Chrome */}
        <motion.div 
          className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-3 ${theme.bgPanel} ${theme.border} border-b transition-colors duration-300`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex gap-1 md:gap-1.5">
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#ff5f57]" />
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#febc2e]" />
            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex-1 ml-2 md:ml-4">
            <div className={`${theme.bgSearch} rounded-lg px-2 md:px-4 py-1 md:py-1.5 text-[10px] md:text-xs ${theme.textMuted} max-w-md mx-auto text-center truncate transition-colors duration-300`}>
              app.ayra.cx
            </div>
          </div>
        </motion.div>

        <div className={`flex min-h-[280px] md:min-h-[520px] ${theme.bg} transition-colors duration-300`}>
          {/* Sidebar - Hidden on mobile */}
          <motion.div 
            className={`w-44 ${theme.bgPanel} ${theme.border} border-r flex-col hidden md:flex transition-colors duration-300`}
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {/* Logo */}
            <motion.div 
              className={`px-3 py-3 flex items-center gap-2 ${theme.borderSoft} border-b transition-colors duration-300`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Image 
                src={isDarkMode ? ayraLogoLight : ayraLogoDark} 
                alt="Ayra AI" 
                width={120}
                height={20}
                className="h-5 w-auto transition-opacity duration-300" 
              />
              <span className={`font-semibold text-[11px] ${theme.text} transition-colors duration-300`}>Ayra AI</span>
              <div className={`ml-auto ${theme.textMutedSoft} text-[10px]`}>‹</div>
            </motion.div>

            {/* Nav Sections */}
            <motion.div 
              className="flex-1 px-2 py-3 space-y-4 overflow-y-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Overview</div>
                <nav className="space-y-0.5">
                  <NavItem 
                    icon={LayoutDashboard} 
                    label="Dashboard" 
                    active={activeView === 'dashboard'} 
                    isDarkMode={isDarkMode} 
                    onClick={() => setActiveView('dashboard')}
                  />
                  <NavItem 
                    icon={BarChart3} 
                    label="Analytics" 
                    active={activeView === 'analytics'} 
                    isDarkMode={isDarkMode} 
                    onClick={() => setActiveView('analytics')}
                  />
                </nav>
              </motion.div>

              {/* Communication */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Communication</div>
                <nav className="space-y-0.5">
                  <NavItem icon={Inbox} label="Inbox" isDarkMode={isDarkMode} />
                  <NavItem 
                    icon={Phone} 
                    label="Calls" 
                    active={activeView === 'calls'} 
                    isDarkMode={isDarkMode} 
                    onClick={() => setActiveView('calls')}
                  />
                  <NavItem icon={AlertTriangle} label="Escalations" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>

              {/* Operations */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Operations</div>
                <nav className="space-y-0.5">
                  <NavItem icon={Calendar} label="Calendar" isDarkMode={isDarkMode} />
                  <NavItem icon={Bot} label="AI Assistant" isDarkMode={isDarkMode} />
                  <NavItem icon={Workflow} label="Workflows" isDarkMode={isDarkMode} />
                  <NavItem icon={Plug} label="Integrations" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>
              {/* Settings */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                <div className={`text-[9px] font-medium ${theme.textMutedSoft} uppercase tracking-wider px-2 mb-1.5 transition-colors duration-300`}>Settings</div>
                <nav className="space-y-0.5">
                  <NavItem icon={Settings} label="Settings" isDarkMode={isDarkMode} />
                  <NavItem icon={HelpCircle} label="Support" isDarkMode={isDarkMode} />
                </nav>
              </motion.div>
            </motion.div>

            {/* Bottom Status */}
            <motion.div 
              className={`px-3 py-2 ${theme.borderSoft} border-t transition-colors duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className={`text-[10px] ${theme.text} font-medium transition-colors duration-300`}>Your Business</span>
                <span className={`text-[9px] ${theme.textMuted} ml-auto transition-colors duration-300`}>Active</span>
              </div>
            </motion.div>
          </motion.div>
          <motion.div 
            className={`flex-1 flex flex-col ${theme.bg} transition-colors duration-300`}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {/* Top Bar */}
            <motion.div 
              className={`h-10 ${theme.bgPanel} ${theme.border} border-b flex items-center px-3 gap-2 transition-colors duration-300`}
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <div className="flex items-center gap-1.5">
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Plus className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Circle className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Calendar className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
              </div>
              
              {/* Search */}
              <div className="flex-1 flex justify-center">
                <div className={`flex items-center gap-2 ${theme.bgSearch} rounded-lg px-3 py-1.5 w-48 transition-colors duration-300`}>
                  <Search className={`w-3 h-3 ${theme.textMutedSoft}`} />
                  <span className={`text-[10px] ${theme.textMutedSoft}`}>Search...</span>
                  <span className={`text-[9px] ${theme.textMutedSoft} ml-auto ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-white'} px-1 rounded transition-colors duration-300`}>⌘K</span>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300 cursor-pointer`}
                >
                  {isDarkMode ? (
                    <Moon className="w-3 h-3 text-primary" />
                  ) : (
                    <Sun className={`w-3 h-3 ${theme.textMuted}`} />
                  )}
                </button>
                <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                  <Bell className={`w-3 h-3 ${theme.textMuted}`} />
                </button>
                <span className={`text-[9px] ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} text-primary px-1.5 py-0.5 rounded font-medium transition-colors duration-300`}>Beta</span>
                <div className="flex items-center gap-1 ml-1">
                  <div className={`w-5 h-5 rounded-full ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-muted'} transition-colors duration-300`} />
                  <span className={`text-[10px] ${theme.text} transition-colors duration-300`}>Demo</span>
                </div>
              </div>
            </motion.div>

            {/* Page Content */}
            <div className="flex-1 p-2 md:p-4 overflow-y-auto">
              <AnimatePresence mode="wait">
                {activeView === 'dashboard' && (
                  <DashboardView key="dashboard" theme={theme} isDarkMode={isDarkMode} />
                )}
                {activeView === 'calls' && (
                  <CallLogsView key="calls" theme={theme} isDarkMode={isDarkMode} />
                )}
                {activeView === 'analytics' && (
                  <AnalyticsView key="analytics" theme={theme} isDarkMode={isDarkMode} />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Dashboard View Component
const DashboardView = ({ theme, isDarkMode }: { theme: any; isDarkMode: boolean }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 20 }}
    transition={{ duration: 0.3 }}
  >
    {/* Header */}
    <motion.div 
      className="flex items-start justify-between mb-2 md:mb-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      <div>
        <div className={`hidden md:flex items-center gap-1 text-[9px] ${theme.textMuted} mb-1 transition-colors duration-300`}>
          <Home className="w-3 h-3" />
          <ChevronRight className="w-2.5 h-2.5" />
          <span>Dashboard</span>
        </div>
        <motion.h1 
          className={`text-sm md:text-lg font-semibold ${theme.text} transition-colors duration-300`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Overview
        </motion.h1>
        <motion.p 
          className={`text-[8px] md:text-[10px] ${theme.textMuted} hidden sm:block transition-colors duration-300`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Monitor your business performance and customer interactions
        </motion.p>
      </div>
      <div className="hidden md:flex items-center gap-2">
        <button 
          className={`flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a] hover:bg-[#2a2a2a]' : 'border-border/50 bg-white hover:bg-muted/30'} border ${theme.text} hover:shadow-sm transition-all cursor-pointer duration-300`}
        >
          <Calendar className="w-3 h-3" />
          View Calendar
        </button>
        <button 
          className="flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-sm transition-all cursor-pointer"
        >
          <Phone className="w-3 h-3" />
          View Calls
        </button>
      </div>
    </motion.div>

    {/* Welcome Card */}
    <motion.div 
      className={`${theme.bgWelcome} rounded-lg md:rounded-xl p-2 md:p-4 mb-2 md:mb-4 ${isDarkMode ? 'border-[#2a2a2a]' : 'border-primary/10'} border transition-colors duration-300`}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <div className="flex justify-between items-start">
        <div>
          <motion.div 
            className={`text-xs md:text-sm ${theme.text} mb-0.5 transition-colors duration-300`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Welcome back, <span className="text-primary font-medium">Trent</span>! ☀️
          </motion.div>
          <motion.div 
            className={`text-[8px] md:text-[10px] ${theme.textMuted} mb-1 md:mb-2 hidden sm:block transition-colors duration-300`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Your AI assistant is handling customer inquiries ✨
          </motion.div>
          <motion.div 
            className={`text-xl md:text-3xl font-bold ${theme.text} transition-colors duration-300`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            06:08 PM
          </motion.div>
          <motion.div 
            className={`text-[7px] md:text-[9px] ${theme.textMuted} uppercase tracking-wide hidden sm:block transition-colors duration-300`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            Tuesday, December 16, 2025
          </motion.div>
        </div>
        <div className="text-right hidden sm:block">
          <div className="flex items-center gap-1 text-lg md:text-2xl font-bold text-primary">
            <Sun className="w-4 md:w-5 h-4 md:h-5" />
            21°C
          </div>
          <div className={`text-[8px] md:text-[9px] ${theme.textMuted} transition-colors duration-300`}>Vancouver</div>
          <div className="hidden md:flex items-center gap-1.5 mt-2">
            <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>This Month</button>
            <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>Export</button>
          </div>
        </div>
      </div>
    </motion.div>

    {/* Metric Cards */}
    <motion.div 
      className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-3 mb-2 md:mb-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <MetricCard label="Conversations" value="552" change="+18" color="primary" isDarkMode={isDarkMode} />
      <MetricCard label="Appointments" value="486" change="+12" color="primary" isDarkMode={isDarkMode} />
      <div className="hidden md:block"><MetricCard label="Calls Answered" value="612" change="+24%" color="primary" isDarkMode={isDarkMode} /></div>
      <div className="hidden md:block"><MetricCard label="Avg. Response" value="&lt; 5 sec" change="-45%" color="primary" isDarkMode={isDarkMode} /></div>
    </motion.div>

    {/* Performance Cards - Hidden on small mobile */}
    <motion.div 
      className="hidden sm:grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-2 md:mb-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <motion.div 
        className={`${theme.bgCard} rounded-lg md:rounded-xl p-2 md:p-3 ${theme.border} border transition-colors duration-300`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="flex items-center gap-2">
          <div className={`w-6 md:w-7 h-6 md:h-7 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-300`}>
            <TrendingUp className="w-3 md:w-3.5 h-3 md:h-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[10px] md:text-[11px] font-medium ${theme.text} truncate transition-colors duration-300`}>Peak performance this week</div>
            <div className={`text-[8px] md:text-[9px] ${theme.textMuted} truncate transition-colors duration-300`}>142 conversations handled <span className="text-primary">(+45%)</span></div>
          </div>
          <button className={`hidden md:block text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>View Details</button>
        </div>
      </motion.div>
      <motion.div 
        className={`${theme.bgCard} rounded-lg md:rounded-xl p-2 md:p-3 ${theme.border} border hidden md:block transition-colors duration-300`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-300`}>
            <Clock className="w-3.5 h-3.5 text-primary" />
          </div>
          <div className="flex-1">
            <div className={`text-[11px] font-medium ${theme.text} transition-colors duration-300`}>Response time improved</div>
            <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>Now averaging <span className="font-medium">under 5 seconds</span>. Customers are happier.</div>
          </div>
          <button className={`text-[9px] px-2 py-1 rounded ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border ${theme.text} transition-colors duration-300`}>View Analytics</button>
        </div>
      </motion.div>
    </motion.div>

    {/* Bottom Section - Simplified on mobile */}
    <motion.div 
      className="hidden md:grid grid-cols-3 gap-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    >
      {/* Top Conversations */}
      <motion.div 
        className={`col-span-2 ${theme.bgCard} rounded-xl p-3 ${theme.border} border transition-colors duration-300`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <div>
              <div className={`text-[11px] font-semibold ${theme.text} transition-colors duration-300`}>Recent Conversations</div>
              <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>Your latest customer interactions</div>
            </div>
          </div>
          <button className="text-[9px] text-primary">View All →</button>
        </div>
        <div className="space-y-2">
          <PerformerRow rank={1} initials="SJ" name="Sarah Johnson" stats="Appointment booked • 2:30 PM" category="Phone Call" change="Completed" isDarkMode={isDarkMode} />
          <PerformerRow rank={2} initials="MC" name="Mike Chen" stats="Question answered • Just now" category="Text Message" change="Active" isDarkMode={isDarkMode} />
          <PerformerRow rank={3} initials="ED" name="Emily Davis" stats="Follow-up scheduled • Tomorrow" category="Instagram DM" change="Pending" isDarkMode={isDarkMode} />
        </div>
      </motion.div>

      {/* Quick Actions */}
      <div className={`${theme.bgCard} rounded-xl p-3 ${theme.border} border transition-colors duration-300`}>
        <div className="flex items-center gap-2 mb-3">
          <Plus className="w-4 h-4 text-primary" />
          <div>
            <div className={`text-[11px] font-semibold ${theme.text} transition-colors duration-300`}>Quick Actions</div>
            <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>Common tasks at your fingertips</div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <QuickAction icon={Inbox} label="View Inbox" shortcut="⌘I" isDarkMode={isDarkMode} />
          <QuickAction icon={Calendar} label="Calendar" shortcut="⌘C" isDarkMode={isDarkMode} />
          <QuickAction icon={Workflow} label="Workflows" shortcut="⌘W" isDarkMode={isDarkMode} />
          <QuickAction icon={Bot} label="AI Settings" shortcut="⌘A" isDarkMode={isDarkMode} />
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// Call Logs View Component
const CallLogsView = ({ theme, isDarkMode }: { theme: any; isDarkMode: boolean }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3 }}
  >
    {/* Header */}
    <motion.div 
      className="flex items-start justify-between mb-2 md:mb-4"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <div>
        <div className={`hidden md:flex items-center gap-1 text-[9px] ${theme.textMuted} mb-1 transition-colors duration-300`}>
          <Home className="w-3 h-3" />
          <ChevronRight className="w-2.5 h-2.5" />
          <Phone className="w-3 h-3" />
          <span>Call Logs</span>
        </div>
        <motion.h1 
          className={`text-sm md:text-lg font-semibold ${theme.text} transition-colors duration-300`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          Call Logs
        </motion.h1>
        <motion.p 
          className={`text-[8px] md:text-[10px] ${theme.textMuted} hidden sm:block transition-colors duration-300`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          View and analyze all voice interactions
        </motion.p>
        <motion.p 
          className={`text-[8px] md:text-[10px] ${theme.textMuted} hidden sm:block mt-1 transition-colors duration-300`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          Showing <span className={theme.text}>20</span> of 192 calls
        </motion.p>
      </div>
      <div className="hidden md:flex items-center gap-2">
        <button 
          className={`flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a] hover:bg-[#2a2a2a]' : 'border-border/50 bg-white hover:bg-muted/30'} border ${theme.text} hover:shadow-sm transition-all cursor-pointer duration-300`}
        >
          <RefreshCw className="w-3 h-3" />
          Refresh
        </button>
        <button 
          className={`flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a] hover:bg-[#2a2a2a]' : 'border-border/50 bg-white hover:bg-muted/30'} border ${theme.text} hover:shadow-sm transition-all cursor-pointer duration-300`}
        >
          <Download className="w-3 h-3" />
          Export
        </button>
      </div>
    </motion.div>

    {/* Stats Cards */}
    <motion.div 
      className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-3 mb-2 md:mb-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <CallStatCard label="TOTAL CALLS" value="192" subtext="192 matching" isDarkMode={isDarkMode} />
      <CallStatCard label="AVG DURATION" value="2:05" subtext="per call" isDarkMode={isDarkMode} />
      <div className="hidden md:block"><CallStatCard label="SUCCESS RATE" value="97%" subtext="187 completed" isDarkMode={isDarkMode} /></div>
      <div className="hidden md:block"><CallStatCard label="POSITIVE" value="85%" subtext="164 calls" isDarkMode={isDarkMode} /></div>
    </motion.div>

    {/* Filters */}
    <motion.div 
      className={`${theme.bgCard} rounded-lg md:rounded-xl p-2 md:p-3 ${theme.border} border mb-2 md:mb-4 transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="flex flex-col md:flex-row gap-2">
        <div className={`flex items-center gap-2 ${theme.bgSearch} rounded-lg px-3 py-1.5 flex-1 transition-colors duration-300`}>
          <Search className={`w-3 h-3 ${theme.textMutedSoft}`} />
          <span className={`text-[10px] ${theme.textMutedSoft}`}>Search calls...</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1 ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border rounded-lg px-2 py-1.5 transition-colors duration-300`}>
            <span className={`text-[10px] ${theme.text}`}>All Status</span>
            <ChevronDown className={`w-3 h-3 ${theme.textMuted}`} />
          </div>
          <div className={`flex items-center gap-1 ${isDarkMode ? 'border-[#2a2a2a] bg-[#1a1a1a]' : 'border-border/50 bg-white'} border rounded-lg px-2 py-1.5 transition-colors duration-300`}>
            <span className={`text-[10px] ${theme.text}`}>All Time</span>
            <ChevronDown className={`w-3 h-3 ${theme.textMuted}`} />
          </div>
          <div className={`hidden md:flex items-center gap-1 ${theme.textMuted} text-[10px] cursor-pointer hover:text-primary transition-colors`}>
            <Filter className="w-3 h-3" />
            <span>Advanced</span>
          </div>
        </div>
      </div>
    </motion.div>

    {/* Call History Table */}
    <motion.div 
      className={`${theme.bgCard} rounded-lg md:rounded-xl ${theme.border} border overflow-hidden transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      <div className="p-3">
        <div className={`text-[11px] font-semibold ${theme.text} transition-colors duration-300`}>Call History</div>
        <div className={`text-[9px] ${theme.textMuted} transition-colors duration-300`}>192 calls found • Page 1 of 10</div>
      </div>
      
      {/* Table Header */}
      <div className={`hidden md:grid grid-cols-5 gap-2 px-3 py-2 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-muted/30'} ${theme.border} border-y transition-colors duration-300`}>
        <div className={`text-[9px] font-medium ${theme.textMuted}`}>Customer</div>
        <div className={`text-[9px] font-medium ${theme.textMuted}`}>Date & Time</div>
        <div className={`text-[9px] font-medium ${theme.textMuted}`}>Duration</div>
        <div className={`text-[9px] font-medium ${theme.textMuted}`}>Status</div>
        <div className={`text-[9px] font-medium ${theme.textMuted} text-right`}>Actions</div>
      </div>

      {/* Table Rows */}
      <div className="divide-y divide-border/20">
        {callData.map((call, index) => (
          <motion.div 
            key={call.id}
            className={`grid grid-cols-2 md:grid-cols-5 gap-2 px-3 py-2.5 ${theme.bgHover} transition-colors duration-200`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.45 + index * 0.05 }}
          >
            <div className="flex items-center gap-2">
              <div className={`w-5 h-5 rounded-full ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center text-[8px] text-primary font-medium`}>
                +{index + 1}
              </div>
              <span className={`text-[10px] ${theme.text} font-medium`}>{call.phone}</span>
            </div>
            <div className={`text-[10px] ${theme.textMuted} flex items-center`}>{call.dateTime}</div>
            <div className={`hidden md:flex text-[10px] ${theme.text} items-center gap-1`}>
              <Clock className="w-3 h-3 text-primary" />
              {call.duration}
            </div>
            <div className="hidden md:flex items-center">
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-500 font-medium">
                {call.status.toLowerCase()}
              </span>
            </div>
            <div className="hidden md:flex items-center justify-end">
              <button className={`w-6 h-6 rounded-md ${theme.bgHover} flex items-center justify-center transition-colors duration-300`}>
                <Eye className={`w-3 h-3 ${theme.textMuted}`} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

// Analytics View Component
const AnalyticsView = ({ theme, isDarkMode }: { theme: any; isDarkMode: boolean }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'agents'>('overview');
  
  // Chart data for Call Volume (area chart)
  const volumeData = [3, 1, 0, 20, 14, 12, 9, 9];
  const days = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];
  
  // Chart data for Success Rate (line chart)
  const successData = [4, 1, 0, 20, 17, 15, 9, 11];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <motion.div 
        className="flex items-start justify-between mb-2 md:mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div>
          <div className={`hidden md:flex items-center gap-1 text-[9px] ${theme.textMuted} mb-1 transition-colors duration-300`}>
            <Home className="w-3 h-3" />
            <ChevronRight className="w-2.5 h-2.5" />
            <BarChart3 className="w-3 h-3" />
            <span>Analytics</span>
          </div>
          <motion.h1 
            className={`text-sm md:text-lg font-semibold ${theme.text} transition-colors duration-300`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            Analytics & Insights
          </motion.h1>
          <motion.p 
            className={`text-[8px] md:text-[10px] ${theme.textMuted} hidden sm:block transition-colors duration-300`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            Deep insights into agent performance and conversation metrics
          </motion.p>
        </div>
      </motion.div>

      {/* Stats Cards with Icons */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-1.5 md:gap-3 mb-2 md:mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <AnalyticsStatCard 
          label="Total Calls" 
          value="192" 
          subtext="All time calls" 
          icon={Phone}
          isDarkMode={isDarkMode} 
        />
        <AnalyticsStatCard 
          label="Avg Duration" 
          value="2:05" 
          subtext="minutes per call" 
          icon={Clock}
          isDarkMode={isDarkMode} 
        />
        <div className="hidden md:block">
          <AnalyticsStatCard 
            label="Success Rate" 
            value="97.9%" 
            subtext="completed calls" 
            icon={Target}
            isDarkMode={isDarkMode} 
          />
        </div>
        <div className="hidden md:block">
          <AnalyticsStatCard 
            label="Positive Sentiment" 
            value="85%" 
            subtext="positive calls" 
            icon={Smile}
            isDarkMode={isDarkMode} 
          />
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div 
        className="mb-2 md:mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.25 }}
      >
        <div className={`inline-flex ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-muted/30'} rounded-lg p-0.5`}>
          {(['overview', 'performance', 'agents'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-1.5 text-[10px] rounded-md transition-all duration-200 capitalize ${
                activeTab === tab 
                  ? 'bg-primary text-primary-foreground font-medium' 
                  : `${theme.textMuted} hover:${theme.text}`
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Charts Section */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-2 md:mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {/* Call Volume Trend - Area Chart */}
        <motion.div 
          className={`${theme.bgCard} rounded-lg md:rounded-xl p-3 ${theme.border} border transition-colors duration-300`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.35 }}
        >
          <div className={`text-[11px] font-semibold ${theme.text} mb-3`}>Call Volume Trend</div>
          <div className="relative h-24 md:h-32">
            {/* Y-axis labels */}
            <div className={`absolute left-0 top-0 h-full flex flex-col justify-between text-[8px] ${theme.textMuted} pr-1`}>
              <span>20</span>
              <span>15</span>
              <span>10</span>
              <span>5</span>
              <span>0</span>
            </div>
            {/* Chart area */}
            <div className="ml-4 h-full relative">
              <svg className="w-full h-full" viewBox="0 0 280 100" preserveAspectRatio="none">
                {/* Grid lines */}
                {[0, 25, 50, 75, 100].map((y) => (
                  <line key={y} x1="0" y1={y} x2="280" y2={y} stroke={isDarkMode ? '#2a2a2a' : '#e5e7eb'} strokeWidth="0.5" />
                ))}
                {/* Area fill with gradient */}
                <defs>
                  <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.05" />
                  </linearGradient>
                </defs>
                <path
                  d={`M0,${100 - (volumeData[0] / 20) * 100} ${volumeData.map((v, i) => `L${(i * 280) / (volumeData.length - 1)},${100 - (v / 20) * 100}`).join(' ')} L280,100 L0,100 Z`}
                  fill="url(#areaGradient)"
                />
                {/* Line */}
                <path
                  d={`M0,${100 - (volumeData[0] / 20) * 100} ${volumeData.map((v, i) => `L${(i * 280) / (volumeData.length - 1)},${100 - (v / 20) * 100}`).join(' ')}`}
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                />
              </svg>
              {/* X-axis labels */}
              <div className={`flex justify-between text-[8px] ${theme.textMuted} mt-1`}>
                {days.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Success Rate Trend - Line Chart */}
        <motion.div 
          className={`${theme.bgCard} rounded-lg md:rounded-xl p-3 ${theme.border} border transition-colors duration-300`}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <div className={`text-[11px] font-semibold ${theme.text} mb-3`}>Success Rate Trend</div>
          <div className="relative h-24 md:h-32">
            {/* Y-axis labels */}
            <div className={`absolute left-0 top-0 h-full flex flex-col justify-between text-[8px] ${theme.textMuted} pr-1`}>
              <span>20</span>
              <span>15</span>
              <span>10</span>
              <span>5</span>
              <span>0</span>
            </div>
            {/* Chart area */}
            <div className="ml-4 h-full relative">
              <svg className="w-full h-full" viewBox="0 0 280 100" preserveAspectRatio="none">
                {/* Grid lines */}
                {[0, 25, 50, 75, 100].map((y) => (
                  <line key={y} x1="0" y1={y} x2="280" y2={y} stroke={isDarkMode ? '#2a2a2a' : '#e5e7eb'} strokeWidth="0.5" />
                ))}
                {/* Line */}
                <path
                  d={`M0,${100 - (successData[0] / 20) * 100} ${successData.map((v, i) => `L${(i * 280) / (successData.length - 1)},${100 - (v / 20) * 100}`).join(' ')}`}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="2"
                />
                {/* Dots */}
                {successData.map((v, i) => (
                  <circle
                    key={i}
                    cx={(i * 280) / (successData.length - 1)}
                    cy={100 - (v / 20) * 100}
                    r="3"
                    fill="hsl(var(--primary))"
                  />
                ))}
              </svg>
              {/* X-axis labels */}
              <div className={`flex justify-between text-[8px] ${theme.textMuted} mt-1`}>
                {days.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Key Insights Section */}
      <motion.div 
        className={`${theme.bgCard} rounded-lg md:rounded-xl p-3 ${theme.border} border transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.45 }}
      >
        <div className={`text-[11px] font-semibold ${theme.text} mb-3`}>Key Insights</div>
        <div className="space-y-2">
          {/* Insight 1 - Strong Performance */}
          <motion.div 
            className={`flex items-center gap-2 p-2 rounded-lg ${isDarkMode ? 'bg-primary/10' : 'bg-primary/5'} ${theme.border} border`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <div className={`w-6 h-6 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} flex items-center justify-center`}>
              <TrendingUp className="w-3 h-3 text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] text-primary font-medium">Strong Performance</div>
              <div className={`text-[9px] ${theme.textMuted}`}>Your AI is maintaining a 97.9% success rate with 192 total calls.</div>
            </div>
          </motion.div>

          {/* Insight 2 - Call Distribution */}
          <motion.div 
            className={`flex items-center gap-2 p-2 rounded-lg ${theme.border} border`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.55 }}
          >
            <div className={`w-6 h-6 rounded-lg ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-muted'} flex items-center justify-center`}>
              <Clock className={`w-3 h-3 ${theme.textMuted}`} />
            </div>
            <div className="flex-1">
              <div className={`text-[10px] ${theme.text} font-medium`}>Call Distribution</div>
              <div className={`text-[9px] ${theme.textMuted}`}>Calls are distributed across 20 different hours of the day.</div>
            </div>
          </motion.div>

          {/* Insight 3 - Top Performer */}
          <motion.div 
            className={`flex items-center gap-2 p-2 rounded-lg ${theme.border} border`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.6 }}
          >
            <div className={`w-6 h-6 rounded-lg ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-muted'} flex items-center justify-center`}>
              <Star className={`w-3 h-3 ${theme.textMuted}`} />
            </div>
            <div className="flex-1">
              <div className={`text-[10px] ${theme.text} font-medium`}>Top Performer</div>
              <div className={`text-[9px] ${theme.textMuted}`}>Your busiest day was Monday with 47 calls handled.</div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Analytics Stat Card Component
const AnalyticsStatCard = ({ label, value, subtext, icon: Icon, isDarkMode }: { 
  label: string; 
  value: string; 
  subtext: string; 
  icon: any;
  isDarkMode: boolean 
}) => (
  <motion.div 
    className={`${isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-border/30'} rounded-lg md:rounded-xl p-2 md:p-3 border transition-colors duration-300 relative`}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className={`absolute top-2 right-2 w-6 h-6 rounded-full ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-muted'} flex items-center justify-center transition-colors duration-300`}>
      <Icon className={`w-3 h-3 ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'}`} />
    </div>
    <div className={`text-[8px] md:text-[9px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} mb-1`}>
      {label}
    </div>
    <div className={`text-lg md:text-2xl font-bold ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}>
      {value}
    </div>
    <div className={`text-[8px] md:text-[9px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}>
      {subtext}
    </div>
  </motion.div>
);

// Call Stats Card Component
const CallStatCard = ({ label, value, subtext, isDarkMode }: { label: string; value: string; subtext: string; isDarkMode: boolean }) => (
  <motion.div 
    className={`${isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-border/30'} rounded-lg md:rounded-xl p-2 md:p-3 border transition-colors duration-300`}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className={`text-[8px] md:text-[9px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} uppercase tracking-wide mb-1`}>
      {label}
    </div>
    <div className={`text-lg md:text-2xl font-bold ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}>
      {value}
    </div>
    <div className={`text-[8px] md:text-[9px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}>
      {subtext}
    </div>
  </motion.div>
);

const NavItem = ({ icon: Icon, label, active = false, isDarkMode = false, onClick }: { icon: any; label: string; active?: boolean; isDarkMode?: boolean; onClick?: () => void }) => (
  <div 
    onClick={onClick}
    className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px] cursor-pointer transition-colors duration-300 ${
      active 
        ? isDarkMode ? "bg-primary/20 text-primary font-medium" : "bg-primary/10 text-primary font-medium"
        : isDarkMode ? "text-[#888888] hover:bg-[#2a2a2a]" : "text-muted-foreground hover:bg-muted/50"
    }`}
  >
    <Icon className="w-3.5 h-3.5" />
    <span>{label}</span>
  </div>
);

const MetricCard = ({ label, value, change, color, isDarkMode = false }: { label: string; value: string; change: string; color: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`${isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-border/30'} rounded-lg md:rounded-xl p-2 md:p-3 border transition-colors duration-300`}
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
  >
    <motion.div 
      className={`hidden md:flex w-6 h-6 rounded-lg ${isDarkMode ? 'bg-primary/20' : 'bg-primary/10'} items-center justify-center mb-2 transition-colors duration-300`}
      initial={{ opacity: 0, rotate: -180 }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <TrendingUp className="w-3 h-3 text-primary" />
    </motion.div>
    <motion.div 
      className="text-[7px] md:text-[9px] text-primary mb-0.5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      {label}
    </motion.div>
    <motion.div 
      className={`text-base md:text-xl font-bold ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      {value}
    </motion.div>
    <motion.div 
      className={`text-[7px] md:text-[9px] ${color === 'primary' ? 'text-primary' : isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      {change}
    </motion.div>
    <div className="hidden md:flex items-end gap-0.5 mt-2 h-4">
      {[30, 45, 35, 50, 40, 60, 55].map((h, i) => (
        <div key={i} className={`flex-1 rounded-sm ${i === 6 ? 'bg-primary' : isDarkMode ? 'bg-primary/30' : 'bg-primary/20'} transition-colors duration-300`} style={{ height: `${h}%` }} />
      ))}
    </div>
  </motion.div>
);

const PerformerRow = ({ rank, initials, name, stats, category, change, isDarkMode = false }: { rank: number; initials: string; name: string; stats: string; category: string; change: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`flex items-center gap-2 py-1.5 ${isDarkMode ? 'border-[#2a2a2a]' : 'border-border/20'} border-b last:border-0 transition-colors duration-300`}
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ x: 5, transition: { duration: 0.2 } }}
  >
    <motion.span 
      className={`text-[9px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} w-3 transition-colors duration-300`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {rank}
    </motion.span>
    <motion.div 
      className={`w-6 h-6 rounded-full ${isDarkMode ? 'bg-[#2a2a2a] text-[#e8e8e8]' : 'bg-muted'} flex items-center justify-center text-[9px] font-medium transition-colors duration-300`}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      {initials}
    </motion.div>
    <div className="flex-1">
      <motion.div 
        className={`text-[10px] font-medium ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        {name}
      </motion.div>
      <motion.div 
        className={`text-[8px] ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {stats}
      </motion.div>
      <motion.div 
        className={`text-[8px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        {category}
      </motion.div>
    </div>
    <motion.div 
      className={`text-[9px] ${change === 'Active' ? 'text-primary' : change === 'Completed' ? 'text-green-500' : isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'}`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.5 }}
    >
      {change}
    </motion.div>
    <div className="flex items-end gap-px h-3 w-8">
      {[40, 55, 45, 60, 50].map((h, i) => (
        <div key={i} className={`flex-1 ${isDarkMode ? 'bg-primary/30' : 'bg-primary/30'} rounded-sm transition-colors duration-300`} style={{ height: `${h}%` }} />
      ))}
    </div>
  </motion.div>
);

const QuickAction = ({ icon: Icon, label, shortcut, isDarkMode = false }: { icon: any; label: string; shortcut: string; isDarkMode?: boolean }) => (
  <motion.div 
    className={`flex flex-col items-center justify-center p-2 rounded-lg ${isDarkMode ? 'border-[#2a2a2a] hover:bg-[#2a2a2a]' : 'border-border/30 hover:bg-muted/30'} border cursor-pointer transition-colors duration-300`}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ scale: 1.1, transition: { duration: 0.2 } }}
  >
    <motion.div
      initial={{ opacity: 0, rotate: -180 }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <Icon className={`w-4 h-4 ${isDarkMode ? 'text-[#888888]' : 'text-muted-foreground'} mb-1 transition-colors duration-300`} />
    </motion.div>
    <motion.span 
      className={`text-[9px] ${isDarkMode ? 'text-[#e8e8e8]' : 'text-foreground'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      {label}
    </motion.span>
    <motion.span 
      className={`text-[8px] ${isDarkMode ? 'text-[#666666]' : 'text-muted-foreground/60'} transition-colors duration-300`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      {shortcut}
    </motion.span>
  </motion.div>
);

export default BusinessDashboardMockup;
